import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { InfoModal } from "../../../../../../core/modal/InfoModal";
import { RubricGeneratorCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const ContextAndInfoStep: React.FC<
  StepComponentProps<RubricGeneratorCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showCustomContext, setShowCustomContext] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const rubricTypeOptions = [
    {
      id: "ANALYTIC",
      emoji: "📊",
      label: t("rubric.type.analytic", "Analitik Rubrik"),
      desc: t(
        "rubric.type.analyticDesc",
        "Her kriter için ayrı performans düzeyleri. Detaylı geri bildirim için ideal.",
      ),
    },
    {
      id: "HOLISTIC",
      emoji: "🎯",
      label: t("rubric.type.holistic", "Bütünsel Rubrik"),
      desc: t(
        "rubric.type.holisticDesc",
        "Genel performansı tek bir ölçekte değerlendirir. Hızlı değerlendirme için.",
      ),
    },
  ];

  const contextOptions = [
    {
      id: "project_presentation",
      emoji: "📊",
      label: t("rubric.context.projectPresentation", "Proje Sunumu"),
      desc: t(
        "rubric.context.projectPresentationDesc",
        "Final proje sergilemeleri, poster oturumları veya öğrenci gösterimleri için",
      ),
    },
    {
      id: "lab_report",
      emoji: "📝",
      label: t("rubric.context.labReport", "Laboratuvar Raporu"),
      desc: t(
        "rubric.context.labReportDesc",
        "Bilim deneyleri, veri analizi veya araştırma dokümantasyonu için",
      ),
    },
    {
      id: "creative_writing",
      emoji: "✍️",
      label: t("rubric.context.creativeWriting", "Yaratıcı Yazma"),
      desc: t(
        "rubric.context.creativeWritingDesc",
        "Denemeler, hikayeler, şiirler veya yaratıcı ifade ödevleri için",
      ),
    },
    {
      id: "research_project",
      emoji: "🔍",
      label: t("rubric.context.researchProject", "Araştırma Projesi"),
      desc: t(
        "rubric.context.researchProjectDesc",
        "Akademik araştırma, kaynak analizi ve bulgular sunumu için",
      ),
    },
    {
      id: "oral_presentation",
      emoji: "🗣️",
      label: t("rubric.context.oralPresentation", "Sözlü Sunum"),
      desc: t(
        "rubric.context.oralPresentationDesc",
        "Konuşma becerileri, sunum teknikleri ve sözlü iletişim için",
      ),
    },
    {
      id: "portfolio_assessment",
      emoji: "📁",
      label: t(
        "rubric.context.portfolioAssessment",
        "Portfolyo Değerlendirmesi",
      ),
      desc: t(
        "rubric.context.portfolioAssessmentDesc",
        "Çalışma koleksiyonları ve ilerleme gösterimi için",
      ),
    },
    {
      id: "group_work",
      emoji: "👥",
      label: t("rubric.context.groupWork", "Grup Çalışması"),
      desc: t(
        "rubric.context.groupWorkDesc",
        "İşbirlikli projeler, takım sunumları veya akran öğrenme etkinlikleri için",
      ),
    },
    {
      id: "art_project",
      emoji: "🎨",
      label: t("rubric.context.artProject", "Sanat Projesi"),
      desc: t(
        "rubric.context.artProjectDesc",
        "Yaratıcı sanat eserleri, tasarım projeleri ve görsel sunumlar için",
      ),
    },
    {
      id: "math_problem_solving",
      emoji: "🔢",
      label: t("rubric.context.mathProblemSolving", "Matematik Problem Çözme"),
      desc: t(
        "rubric.context.mathProblemSolvingDesc",
        "Matematiksel düşünme, problem çözme stratejileri için",
      ),
    },
    {
      id: "scientific_experiment",
      emoji: "🧪",
      label: t("rubric.context.scientificExperiment", "Bilimsel Deney"),
      desc: t(
        "rubric.context.scientificExperimentDesc",
        "Laboratuvar çalışmaları, deney tasarımı ve bilimsel süreç için",
      ),
    },
  ];

  const handleRubricTypeSelect = (rubricType: string) => {
    onDataChange({ rubricType: rubricType as any });
  };

  console.log(data);

  // Helper function to check if current context is a predefined option
  const isPredefinedContext = (context: string | undefined) => {
    if (!context) return false;
    return contextOptions.some((option) => option.label === context);
  };

  // Helper function to check if "Others" option should be selected
  const isOthersSelected = () => {
    return (
      showCustomContext ||
      (!isPredefinedContext(data.contextOfUse) && !!data.contextOfUse)
    );
  };

  const handleContextSelect = (contextLabel: string) => {
    onDataChange({ contextOfUse: contextLabel });
    // Hide custom context when a preset is selected
    setShowCustomContext(false);
  };

  const handleOthersClick = () => {
    onDataChange({ contextOfUse: "" });
    setShowCustomContext(true);
  };

  // Prepare examples for the InfoModal
  const infoModalExamples = [
    {
      emoji: "📊",
      title: t("rubric.context.projectPresentation", "Proje Sunumu"),
      description: t(
        "rubric.context.projectPresentationDesc",
        "Final proje sergilemeleri, poster oturumları veya öğrenci gösterimleri için",
      ),
    },
    {
      emoji: "📝",
      title: t("rubric.context.labReport", "Laboratuvar Raporu"),
      description: t(
        "rubric.context.labReportDesc",
        "Bilim deneyleri, veri analizi veya araştırma dokümantasyonu için",
      ),
    },
    {
      emoji: "✍️",
      title: t("rubric.context.creativeWriting", "Yaratıcı Yazma"),
      description: t(
        "rubric.context.creativeWritingDesc",
        "Denemeler, hikayeler, şiirler veya yaratıcı ifade ödevleri için",
      ),
    },
    {
      emoji: "👥",
      title: t("rubric.context.groupWork", "Grup Çalışması"),
      description: t(
        "rubric.context.groupWorkDesc",
        "İşbirlikli projeler, takım sunumları veya akran öğrenme etkinlikleri için",
      ),
    },
  ];

  // Prepare examples for the Rubric Type InfoModal
  const rubricTypeInfoExamples = [
    {
      emoji: "📊",
      title: t("rubric.type.analytic", "Analitik Rubrik"),
      description: t(
        "rubric.type.analyticDesc",
        "Her kriter için ayrı performans düzeyleri. Detaylı geri bildirim için ideal.",
      ),
    },
    {
      emoji: "🎯",
      title: t("rubric.type.holistic", "Bütünsel Rubrik"),
      description: t(
        "rubric.type.holisticDesc",
        "Genel performansı tek bir ölçekte değerlendirir. Hızlı değerlendirme için.",
      ),
    },
  ];

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("rubric.steps.context.title", "Rubrik Tipi ve Kullanım Bağlamı")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t(
            "rubric.steps.context.description",
            "Rubrik tipini seçin ve nerede kullanılacağını belirtin",
          )}
        </p>
      </div>

      {/* Rubric Type Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("rubric.form.rubricType", "Rubrik Tipi")}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rubricTypeOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.rubricType === option.id
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleRubricTypeSelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.rubricType === option.id
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.rubricType === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Context Selection Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("rubric.form.contextSelection", "Kullanım Bağlamı")}
          </label>
          <button
            onClick={() => setShowInfoModal(true)}
            className={`inline-flex items-center justify-center w-5 h-5 rounded-full bg-${themeColor}-100 dark:bg-${themeColor}-900/30 hover:bg-${themeColor}-200 dark:hover:bg-${themeColor}-900/50 transition-colors duration-200 group`}
            title={t(
              "rubric.info.contextGuide.info",
              "Bağlam seçimi hakkında bilgi",
            )}
          >
            <svg
              className={`w-3 h-3 text-${themeColor}-600 dark:text-${themeColor}-400 group-hover:scale-110 transition-transform duration-200`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </button>
        </div>

        {/* Context Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {contextOptions.map((option) => (
            <div
              key={option.label}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.contextOfUse === option.label
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleContextSelect(option.label)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.contextOfUse === option.label
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.contextOfUse === option.label && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Others Option */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isOthersSelected()
                ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handleOthersClick}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                  isOthersSelected()
                    ? `border-${themeColor}-500 bg-${themeColor}-500`
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isOthersSelected() && (
                  <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-lg">✏️</span>
                  <h3 className="font-medium text-base">
                    {t("rubric.context.others", "Diğer")}
                  </h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {t(
                    "rubric.context.othersDesc",
                    "Kendi bağlam bilgilerinizi belirtin",
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Context Textarea */}
      {showCustomContext && (
        <div className="space-y-3">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t(
              "rubric.form.customContext",
              "Bu rubriğin nerede ve nasıl kullanılacağını açıklayın...",
            )}
          </label>
          <textarea
            value={data.contextOfUse || ""}
            onChange={(e) => onDataChange({ contextOfUse: e.target.value })}
            className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
            placeholder={t(
              "rubric.form.customContextPlaceholder",
              "Bu rubriğin nerede ve nasıl kullanılacağını açıklayın...",
            )}
            rows={4}
          />
        </div>
      )}

      {/* Context Info Modal */}
      <InfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        title={t(
          "rubric.info.contextGuide.title",
          "Doğru bağlamı nasıl seçersiniz",
        )}
        description={t(
          "rubric.info.contextGuide.description",
          "Öğrencilerin tamamlayacağı özel ödev veya görevi düşünün. Bu, AI'nın öğrenme ortamını anlamasına ve uygun değerlendirme kriterlerini oluşturmasına yardımcı olur. Yaygın bağlamlardan seçin veya benzersiz durumunuzu açıklayın.",
        )}
        examples={infoModalExamples}
        themeColor={themeColor}
        icon="💡"
      />
    </div>
  );
};
